/**
 * 会话历史和上下文的纯函数（docs/语音助手.md 第 4 部分，与 iOS VoiceAgent / TokenEstimate 一致）：
 * token 估算、按预算截断上下文、服务端消息 → 界面条目 / 发给模型的消息。
 */
import type { ChatMessage, ChatProvider } from "./providers";
import { sendsReasoningBack } from "./providers";

/** 汉字（及假名、韩文），与后端 services/assistant-history.ts 的 CJK 一致 */
const CJK = /[㐀-䶿一-鿿豈-﫿぀-ヿ가-힯]/g;

/** token 估算（与后端 estimateTokens 一致）：汉字 1 个、其它字符 4 个算 1 个，每条消息另加 4 */
export function estimateTokens(text: string): number {
  const cjk = (text.match(CJK) ?? []).length;
  return cjk + Math.ceil((text.length - cjk) / 4) + 4;
}

/** 一条消息的 token（与后端保存时的 tokens 字段一致：内容 + 工具调用的 JSON） */
export function messageTokens(content: string, toolCalls?: unknown[] | null): number {
  return estimateTokens(content) + (toolCalls && toolCalls.length ? estimateTokens(JSON.stringify(toolCalls)) : 0);
}

/** 上下文 token 预算：qwen-max 16k，其它 60k */
export function contextBudget(model: string): number {
  return model === "qwen-max" ? 16_000 : 60_000;
}

/** 上下文最多条数 */
export const MAX_HISTORY = 400;

/**
 * 从第几条开始保留：从最新往前累加，超出预算或条数前，最后一个落在用户消息上的位置；最新的一轮总要保留
 * （只在用户消息处截断，不拆开一轮工具调用）
 */
export function keepFrom(roles: string[], tokens: number[], budget: number, maxCount: number): number {
  let total = 0;
  let keep = roles.length;
  for (let i = roles.length - 1; i >= 0; i--) {
    total += tokens[i] ?? 0;
    if (total > budget || roles.length - i > maxCount) break;
    if (roles[i] === "user") keep = i;
  }
  if (keep === roles.length) {
    const last = roles.lastIndexOf("user");
    if (last >= 0) keep = last;
  }
  return keep;
}

/** 服务端保存的消息（GET assistant/conversation，键保持 snake_case） */
export interface ServerMessage {
  id: string;
  seq?: number | null;
  role: "user" | "assistant" | "tool" | string;
  content?: string | null;
  tool_calls?: ToolCallJSON[] | null;
  tool_call_id?: string | null;
  name?: string | null;
  attachments?: AttachmentMeta[] | null;
  reasoning?: string | null;
  tokens?: number | null;
  created_at?: number | null;
}

export interface ToolCallJSON {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

/** 消息里记录的附件信息 */
export interface AttachmentMeta {
  kind: "image" | "file" | string;
  name: string;
  mime?: string;
  size?: number;
  asset_id?: string;
  /** PDF 的总页数 */
  page_count?: number;
}

/** 发给模型的一条上下文消息：seq 为服务端序号（还没保存时为 null） */
export interface ContextMessage {
  id: string;
  seq: number | null;
  tokens: number;
  json: ChatMessage;
  /** 助手消息的思考过程（DeepSeek 开启思考时要发回去） */
  reasoning?: string | null;
}

/** 服务端消息 → 发给模型的格式 */
export function toContextMessage(m: ServerMessage): ContextMessage | null {
  if (!m.id || !m.role) return null;
  const content = m.content ?? "";
  const json: ChatMessage = { role: m.role, content };
  if (m.tool_calls && m.tool_calls.length) json.tool_calls = m.tool_calls;
  if (m.role === "tool") json.tool_call_id = m.tool_call_id ?? "";
  const tokens = typeof m.tokens === "number" ? m.tokens : messageTokens(content, m.tool_calls);
  return { id: m.id, seq: m.seq ?? null, tokens, json, reasoning: m.reasoning ?? null };
}

/** 发给模型的上下文：DeepSeek 开启思考时，助手消息要带上之前的思考过程（没有的写空字符串） */
export function requestMessages(context: ContextMessage[], provider: ChatProvider, thinking: boolean): ChatMessage[] {
  if (!(sendsReasoningBack(provider) && thinking)) return pairToolCalls(context.map((m) => m.json));
  return pairToolCalls(context.map((m) => (m.json.role === "assistant" ? { ...m.json, reasoning_content: m.reasoning ?? "" } : m.json)));
}

/** 补给没有结果的工具调用的内容（中途停止、提前结束时留下的） */
export const UNANSWERED_TOOL_RESULT = "这次操作被停止了，没有执行完。";

/**
 * 工具调用和结果配对（与 iOS VoiceAgent.pairToolCalls、安卓 HistoryLogic.pairToolCalls 一致）：
 * 每条带 tool_calls 的助手消息后面紧跟每个调用的结果，缺的补一条"被停止了"；没有对应调用的工具消息去掉。
 * 否则 DeepSeek 等服务会报 400（中途停止时历史里可能已经存了没有结果的调用）。
 */
export function pairToolCalls(messages: ChatMessage[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  let i = 0;
  while (i < messages.length) {
    const m = messages[i++]!;
    if (m.role === "tool") continue;
    out.push(m);
    const calls = m.role === "assistant" ? ((m.tool_calls as ToolCallJSON[] | undefined) ?? []) : [];
    if (!calls.length) continue;
    const results = new Map<string, ChatMessage>();
    for (; i < messages.length && messages[i]!.role === "tool"; i++) {
      const id = messages[i]!.tool_call_id as string;
      if (!results.has(id)) results.set(id, messages[i]!);
    }
    for (const c of calls) out.push(results.get(c.id) ?? { role: "tool", content: UNANSWERED_TOOL_RESULT, tool_call_id: c.id });
  }
  return out;
}

/** 附件名前的标记（界面上换成回形针图标） */
export const ATTACHMENT_MARK = "📎 ";

/** 用户消息在界面上的样子：附件的文字内容不显示，只显示附件名（与 iOS VoiceAgent.displayText 一致；新旧两种附件标题都适用） */
export function displayText(content: string, attachments?: AttachmentMeta[] | null): string {
  let text = content;
  const cut = text.indexOf("\n\n【附件：");
  if (cut >= 0) text = text.slice(0, cut);
  text = text.replace(/\n?\[图片：[^\]]*\]/g, "");
  const names = (attachments ?? []).map((a) => a?.name).filter((n): n is string => typeof n === "string");
  return [text.trim(), ...names.map((n) => `${ATTACHMENT_MARK}${n}`)].filter((s) => s.length > 0).join("\n");
}

/** 拼进用户消息的附件 */
export interface SavedAttachment {
  name: string;
  text?: string | null;
  imageCount: number;
  /** 上传成功后的附件 id */
  assetId?: string;
  /** 上传失败（重试后仍失败）：告诉模型没有附件 id */
  uploadFailed?: boolean;
  /** PDF 的总页数 */
  pageCount?: number;
  /** text 里包含到第几页 */
  textPages?: number;
}

/** 与 iOS AssistantAttachment.uploadFailedNote 一致 */
export const UPLOAD_FAILED_NOTE = "原件上传失败，没有附件 id，要用原件（比如做成阅读内容）请家长在对话里重新发送这个附件";

/** 附件标题里的说明：上传成功时带附件 id，PDF 带页数，例如"（附件 id：…，PDF 共 11 页）"；上传失败时写明没有附件 id（与 iOS AssistantAttachment.headerInfo 一致） */
export function attachmentHeaderInfo(a: SavedAttachment): string {
  const parts: string[] = [];
  if (a.assetId) parts.push(`附件 id：${a.assetId}`);
  else if (a.uploadFailed) parts.push(UPLOAD_FAILED_NOTE);
  if (a.pageCount != null) {
    let s = `PDF 共 ${a.pageCount} 页`;
    if (a.textPages != null && a.textPages < a.pageCount && a.text) s += `，这里只有前 ${a.textPages} 页`;
    parts.push(s);
  }
  return parts.length ? `（${parts.join("，")}）` : "";
}

/**
 * 保存（也是发给模型）的用户消息文字（与 iOS VoiceAgent.savedText 一致）：附件提取出的文字拼在后面（可以全文检索），图片只留占位。
 * 上传成功的附件在标题里带附件 id（PDF 另带页数）："【附件：a.pdf（附件 id：…，PDF 共 11 页）】"、"[图片：b.jpg（附件 id：…）]"
 */
export function savedUserText(text: string, attachments: SavedAttachment[]): string {
  let saved = text;
  for (const a of attachments) {
    const info = attachmentHeaderInfo(a);
    if (a.text) saved += `\n\n【附件：${a.name}${info}】\n${a.text}`;
    // 没提取到内容的文件也告诉模型附件 id（或上传失败）
    else if (a.imageCount === 0 && (a.assetId || a.uploadFailed)) saved += `\n\n【附件：${a.name}${info}】`;
    if (a.imageCount > 0) saved += `\n[图片：${a.name}${info}]`;
  }
  return saved;
}

/** 摘要拼进系统提示 */
export function withSummary(prompt: string, summary: string | null | undefined): string {
  if (!summary) return prompt;
  return `${prompt}\n\n【之前对话的摘要】\n${summary}\n\n更早的细节可以用 search_history 搜索。`;
}

/** 确认弹窗里显示的参数摘要 */
export function describeArgs(args: Record<string, unknown>): string {
  return Object.keys(args)
    .sort()
    .map((k) => {
      const v = args[k];
      if (Array.isArray(v)) return `${k}：${v.length} 项`;
      return `${k}：${typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}`;
    })
    .join("\n");
}
